import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { ChipRadioGroup } from "@/components/ui/chip-radio";
import { Input, type InputProps } from "@/components/ui/input";
import { formatTimeForDisplay, formatTimeTyping, needsAmPm } from "@/lib/format";

export interface TimeFieldProps
  extends Omit<InputProps, "value" | "onChange" | "onBlur" | "type"> {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Classes for a box around the time box alone (its width), so they don't
   *  squeeze the AM or PM question under it. */
  boxClassName?: string;
}

export const TimeField = forwardRef<HTMLInputElement, TimeFieldProps>(
  (
    {
      value,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      placeholder = "2:30 PM or 14:30",
      clearable = true,
      boxClassName,
      ...props
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const askRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
    // "1:00" with no AM or PM is asked once the field is left (needsAmPm),
    // rather than read as 1 AM. Kept in the page while typed, only hidden, so
    // leaving the field adds nothing a dialog's focus trap reacts to.
    const ask = needsAmPm(value);
    const box = (
      <Input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        maxLength={8}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(formatTimeTyping(e.target.value))}
        onKeyDown={(e) => {
          // Tab from "1:00" goes to its AM and PM, not past them.
          if (e.key === "Tab" && !e.shiftKey && ask) {
            e.preventDefault();
            flushSync(() => setFocused(false));
            askRef.current?.querySelector<HTMLElement>('[role="radio"]')?.focus();
          }
          onKeyDown?.(e);
        }}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={() => {
          setFocused(false);
          const display = ask ? "" : formatTimeForDisplay(value);
          if (display && display !== value) onChange(display);
          onBlur?.();
        }}
        clearable={clearable}
        onClear={() => onChange("")}
        {...props}
      />
    );
    return (
      <>
        {boxClassName ? <div className={boxClassName}>{box}</div> : box}
        {ask ? (
          <div
            ref={askRef}
            hidden={focused}
            className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 [&[hidden]]:hidden"
            // A click or tap leaves the focus where it is (on an iPad, no
            // keyboard pops up), so a pick doesn't drop it when this goes.
            onMouseDown={(e) => e.preventDefault()}
          >
            <span className="text-xs text-muted-foreground">AM or PM?</span>
            <ChipRadioGroup
              className="justify-start"
              size="sm"
              ariaLabel={`${value.trim()} AM or PM`}
              value=""
              // Picked, the field is done with: its check runs as on leaving it.
              onValueChange={(period) => {
                // Picked with the focus on it (keyboard, screen reader): the
                // focus goes back to the box instead of being lost with it.
                const back = askRef.current?.contains(document.activeElement);
                onChange(`${value.trim()} ${period}`);
                onBlur?.();
                if (back) inputRef.current?.focus();
              }}
              options={[
                { value: "AM", label: `${value.trim()} AM` },
                { value: "PM", label: `${value.trim()} PM` },
              ]}
            />
          </div>
        ) : null}
      </>
    );
  },
);
TimeField.displayName = "TimeField";
